import React, {ReactNode, useEffect, useLayoutEffect, useRef, useState} from 'react';
import ChevronLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import ChevronRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {EstimateTreeDto} from '../../../shared/dtos/estimate-tree.dto';
import {EstimateGateDetailsDto} from '../../../shared/dtos/estimate-gate.dto';

/*
 * A work item's photos (WorkItemsView.kt PhotoPager): one at a time at its
 * own shape, arrows at the edges and the count at the bottom, wrapping round.
 * Once the strip can give two photos 320 each (SQ-596) it shows two side by
 * side; the arrows still move one photo at a time. An item with one photo
 * keeps the single larger photo. A tap opens the photo.
 *
 * The frame is sized around the photo, never the photo inside a frame
 * (SQ-640): a single photo is the strip's width at the photo's own shape;
 * two side by side share the width in proportion to their shapes, so both
 * come out the same height and neither has bars beside it. No photo is
 * taller than PHOTO_MAX_HEIGHT (SQ-716, as Android's): one that would be is
 * that tall and narrower, centred, and a pair narrows together. Nothing
 * caps the width.
 */

export interface Photo {
  key: string;
  url: string;
  /** The stored image, for the work item's own photos (Delete, Edit Picture). */
  imageId?: number;
  /** A connected gate's photo, shown after the item's own with its gate's number. */
  gate?: number;
  /** That gate and its stored image, for Edit Picture in the viewer (SQ-657). */
  gateId?: number;
  gateImageId?: number;
}

/** A gate as far as the photos need it: the customer's copy (SQ-337) carries none of its photos. */
type PhotoGate = Pick<EstimateGateDetailsDto, 'id'> & {
  associatedTrees?: {id: number}[]; images?: EstimateGateDetailsDto['images']; hiddenImages?: EstimateGateDetailsDto['hiddenImages'];
};

/** The item's own photos, then its gates' (each badged with its gate's number, as the map numbers them), less those it hid. */
export const photosOf = (tree: EstimateTreeDto, gates: PhotoGate[]): Photo[] => {
  const own: Photo[] = tree.images.map(i => ({key: `t${i.id}`, url: i.imageUrl, imageId: i.id}));
  const sorted = [...gates].sort((a, b) => a.id - b.id);
  const fromGates = sorted
    .map((g, i) => ({g, number: i + 1}))
    .filter(({g}) => g.associatedTrees?.some(t => t.id === tree.id))
    .flatMap(({g, number}) => (g.images ?? [])
      .filter(i => !g.hiddenImages?.some(h => h.treeId === tree.id && h.imageId === i.id))
      .map(i => ({key: `g${g.id}-${i.id}`, url: i.imageUrl, gate: number, gateId: g.id, gateImageId: i.id})));
  return [...own, ...fromGates];
};

/** Android's "Remove Gate N picture?" (WorkItemsView.kt): a gate's photo leaves one work item, never the gate. */
export const hideGatePhotoConfirm = (gate?: number) => {
  const name = gate !== undefined ? `Gate ${gate}` : 'the gate';
  return {
    title: `Remove ${gate !== undefined ? `Gate ${gate}` : 'gate'} picture?`,
    text: `This removes the picture from this work item only. It stays on ${name} and on the other work items connected to it.`,
    confirm: 'Remove',
  };
};

/** SQ-716: the tallest a work item photo is drawn, one or a pair (Android's PHOTO_MAX_HEIGHT, in dp). */
export const PHOTO_MAX_HEIGHT = 440;
const TWO_UP_MIN = 320;
const TWO_UP_GAP = 8;

const useWidth = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(() => setWidth(el.clientWidth));
    observer.observe(el);
    setWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
};

export const PhotoPager = ({photos, overlay, onOpen}: {
  photos: Photo[];
  /** Drawn on each photo shown: edit mode's Delete and Edit Picture. */
  overlay?: (photo: Photo) => ReactNode;
  onOpen?: (photo: Photo) => void;
}) => {
  const [index, setIndex] = useState(0);
  const [ratio, setRatio] = useState<Record<string, number>>({});
  const [failed, setFailed] = useState<Record<string, number>>({});
  const [dragStart, setDragStart] = useState<number | null>(null);
  // A swipe ends in a click on the photo too: that one does not open it.
  const swiped = useRef(false);
  const [ref, width] = useWidth();

  // A photo taken while the card is up is shown (Android follows new photos); a removed one never leaves the index past the end.
  const ownCount = photos.filter(p => p.gate === undefined).length;
  const seen = useRef(ownCount);
  useEffect(() => {
    if (ownCount > seen.current) setIndex(Math.max(0, ownCount - 1));
    seen.current = ownCount;
  }, [ownCount]);
  const at = Math.min(index, Math.max(0, photos.length - 1));

  const twoUp = photos.length > 1 && width >= TWO_UP_MIN * 2 + TWO_UP_GAP;
  const shown = twoUp ? [photos[at], photos[(at + 1) % photos.length]] : [photos[at]];
  const go = (step: number) => setIndex(i => (Math.min(i, photos.length - 1) + step + photos.length) % photos.length);
  const many = photos.length > (twoUp ? 2 : 1);
  const count = twoUp ? `${at + 1}-${(at + 1) % photos.length + 1}/${photos.length}` : `${at + 1}/${photos.length}`;
  if (photos.length === 0) return null;
  const shapeOf = (photo: Photo) => ratio[photo.key] ?? 3 / 4;
  // The row is as wide as the photos are at the cap (or less), so the arrows stay on their edges.
  const rowMax = shown.reduce((sum, p) => sum + PHOTO_MAX_HEIGHT * shapeOf(p), 0) + (twoUp ? TWO_UP_GAP : 0);
  return (
    <div ref={ref} className="er-photos">
      <div className={twoUp ? 'er-photos-row er-photos-row--two' : 'er-photos-row'} style={{maxWidth: rowMax}}
           onPointerDown={e => { setDragStart(e.clientX); swiped.current = false; }}
           onPointerUp={e => {
             if (dragStart !== null && photos.length > 1 && Math.abs(e.clientX - dragStart) > 40) {
               swiped.current = true;
               go(e.clientX < dragStart ? 1 : -1);
             }
             setDragStart(null);
           }}>
        {shown.map(photo => {
          const shape = shapeOf(photo);
          return (
          <div key={photo.key} className="er-pager" style={twoUp ? {flex: `${shape} 1 0`, aspectRatio: String(shape)} : {aspectRatio: String(shape)}}>
            {failed[photo.key] !== undefined
              ? (
                <button type="button" className="er-pager-failed" onClick={() => setFailed(f => {
                  const next = {...f};
                  delete next[photo.key];
                  return next;
                })}>Photo didn't load. Tap to try again.</button>
              )
              : <img src={photo.url} alt="" draggable={false}
                     onClick={() => { if (!swiped.current) onOpen?.(photo); }}
                     onLoad={e => {
                       const img = e.currentTarget;
                       if (img.naturalWidth && img.naturalHeight) setRatio(r => ({...r, [photo.key]: img.naturalWidth / img.naturalHeight}));
                     }}
                     onError={() => setFailed(f => ({...f, [photo.key]: Date.now()}))}/>}
            {photo.gate !== undefined && <span className="er-gate-badge">Gate {photo.gate}</span>}
            {overlay?.(photo)}
          </div>
          );
        })}
        {many && <>
          <button type="button" className="er-pager-arrow er-pager-arrow--left" onClick={() => go(-1)} aria-label="Previous photo"><ChevronLeftIcon/></button>
          <button type="button" className="er-pager-arrow er-pager-arrow--right" onClick={() => go(1)} aria-label="Next photo"><ChevronRightIcon/></button>
        </>}
        {!twoUp && photos.length > 1 && <span className="er-pager-count">{count}</span>}
      </div>
      {twoUp && photos.length > 2 && <div className="er-photos-count">{count}</div>}
    </div>
  );
};
